import React, { useEffect, useState } from "react";
import { Spin } from "antd";
import MachineInfoCard from "./MachineInfoCard";
import ProductLineChart from "./ProductLineChart";
import PieChartMachineStatus from "./PieChartMachineStatus";
import CycleTimeLineChart from "./CycleTimeLineChart";
import CycleTimeSummaryCards from "./CycleTimeSummaryCards";
import LastTenDaysProductionChart from "./LastTenDaysProductionChart ";

const MachineDetailsPage = ({ machine, department, machineId }) => {
  const machineStatus = machine?.status === "active" ? "Aktif" : "Aktif Değil";
  const [productData, setProductData] = useState({ labels: [], values: [] });
  const [cycleTimeData, setCycleTimeData] = useState({
    labels: [],
    values: [],
  });
  const [machineCycleTimeData, setMachineCycleTimeData] = useState({
    labels: [],
    values: [],
  });
  const [machineDelayTimeData, setMachineDelayTimeData] = useState({
    labels: [],
    values: [],
  });
  const [stopped, setStopped] = useState({ labels: [], values: [] });
  const [shortStopped, setShortStopped] = useState({ labels: [], values: [] });
  const [lastProductionCount, setLastProductionCount] = useState([null]);
  const [loading, setLoading] = useState(false);
  const [timeRange, setTimeRange] = useState("last-hour");
  const apiUrl = process.env.REACT_APP_MONGO_URL;

  const machineInfo = [
    { title: "Makine Adı", value: machine?.name },
    { title: "Makine Kodu", value: machine?.code },
    { title: "Başlama Saati", value: machine?.startTime || 0 },
    { title: "Operatör", value: department },
    { title: "Çalışma Durumu", value: machineStatus },
    { title: "Makine Yılı", value: machine?.machineYear },
  ];

  const orderInfo = [
    { title: "İş Emri Numarası", value: "CN-RT-114364" },
    { title: "Planlanan Ürün Adedi", value: `12,547/${lastProductionCount}` },
  ];

  const lastTenDaysData = [
    { date: "2024-01-01", planned: 100, produced: 90 },
    { date: "2024-01-02", planned: 110, produced: 100 },
    { date: "2024-01-03", planned: 120, produced: 124 },
    { date: "2024-01-04", planned: 115, produced: 100 },
    { date: "2024-01-05", planned: 106, produced: 107 },
    { date: "2024-01-06", planned: 111, produced: 109 },
    { date: "2024-01-07", planned: 110, produced: 103 },
    { date: "2024-01-08", planned: 118, produced: 119 },
    { date: "2024-01-09", planned: 115, produced: 107 },
    { date: "2024-01-10", planned: 104, produced: 98 },
  ];

  const handleTimeRangeChange = (e) => {
    setTimeRange(e.target.value);
    console.log("Seçilen Zaman Aralığı:", e.target.value);
  };

  useEffect(() => {
    if (!apiUrl || !machineId) return;

    const fetchProductionCounts = async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `${apiUrl}/api/production-data?range=${timeRange}`
        );
        if (!response.ok) {
          throw new Error("Verileri getirme hatası");
        }

        const allData = await response.json();

        if (!allData || allData.length === 0) {
          // console.warn("Boş veri geldi.");
          setProductData({ labels: [], values: [] });
          return;
        }

        // Makine ID'ye göre filtreleme
        const filteredData = allData.filter(
          (item) => item.machineId === machineId
        );

        // Zamanı UTC formatında göster
        setProductData({
          labels: filteredData.map((item) => {
            const date = new Date(item.countTime);
            return `${date.getUTCHours().toString().padStart(2, "0")}:${date
              .getUTCMinutes()
              .toString()
              .padStart(2, "0")}:${date
              .getUTCSeconds()
              .toString()
              .padStart(2, "0")}`;
          }),
          values: filteredData.map((item) => item.productionCount),
        });

        const lastProductionCount =
          filteredData[filteredData.length - 1]?.productionCount || 0;
        setLastProductionCount(lastProductionCount);
      } catch (error) {
        console.error("Veri hatası:", error);
      } finally {
        setLoading(false);
      }
    };

    // İlk veri çekme
    fetchProductionCounts();
    // 30 saniyede bir veri çek
    if (timeRange === "last-hour") {
      const intervalId = setInterval(fetchProductionCounts, 60000);
      // Temizlik işlemi: Komponent unmount olduğunda interval'i temizle
      return () => clearInterval(intervalId);
    }
  }, [apiUrl, machineId, timeRange]);

  useEffect(() => {
    if (!apiUrl || !machineId) return;

    const fetchCycleTimes = async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `${apiUrl}/api/cycleTimes?range=today&machineId=${machineId}`
        );
        if (!response.ok) {
          throw new Error("Verileri getirme hatası");
        }

        const allCycleData = await response.json();

        if (!allCycleData || allCycleData.length === 0) {
          // console.warn("Boş veri geldi.");
          setCycleTimeData({ labels: [], values: [] });
          return;
        }

        // Veriyi işleme
        setCycleTimeData({
          labels: allCycleData.map((item) => {
            const date = new Date(item.createdTime);
            return `${date.getUTCHours().toString().padStart(2, "0")}:${date
              .getUTCMinutes()
              .toString()
              .padStart(2, "0")}`;
          }),
          values: allCycleData.map((item) => {
            const parts = item.cycleTime.split(":").map(Number); // "0:00:04" -> [0, 0, 4]
            const seconds = parts.reduce(
              (acc, part, index) =>
                acc + part * Math.pow(60, parts.length - 1 - index),
              0
            ); // Saniyeye çevir
            return seconds;
          }), // `cycleTime` string ise float'a çeviriyoruz
        });
      } catch (error) {
        console.error("Veri hatası:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCycleTimes();
  }, [apiUrl, machineId, timeRange]);

  useEffect(() => {
    if (!apiUrl || !machineId) return;

    const fetchMachineCycleTimes = async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `${apiUrl}/api/machineCycleTimes?range=today&machineId=${machineId}`
        );
        if (!response.ok) {
          throw new Error("Verileri getirme hatası");
        }

        const allMachineCycleData = await response.json();

        if (!allMachineCycleData || allMachineCycleData.length === 0) {
          // console.warn("Boş veri geldi.");
          setMachineCycleTimeData({ labels: [], values: [] });
          return;
        }

        // Veriyi işleme
        setMachineCycleTimeData({
          labels: allMachineCycleData.map((item) => {
            const date = new Date(item.createdTime);
            return `${date.getUTCHours().toString().padStart(2, "0")}:${date
              .getUTCMinutes()
              .toString()
              .padStart(2, "0")}`;
          }),
          values: allMachineCycleData.map((item) => {
            const parts = item.cycleTime.split(":").map(Number); // "0:00:04" -> [0, 0, 4]
            const seconds = parts.reduce(
              (acc, part, index) =>
                acc + part * Math.pow(60, parts.length - 1 - index),
              0
            ); // Saniyeye çevir
            return seconds;
          }), // `machineCycleTime` string ise float'a çeviriyoruz
        });
      } catch (error) {
        console.error("Veri hatası:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchMachineCycleTimes();
  }, [apiUrl, machineId, timeRange]);

  useEffect(() => {
    if (!apiUrl || !machineId) return;

    const fetchMachineDelayTimes = async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `${apiUrl}/api/machineDelayTimes?range=today&machineId=${machineId}`
        );
        if (!response.ok) {
          throw new Error("Verileri getirme hatası");
        }

        const allMachineDelayData = await response.json();

        if (!allMachineDelayData || allMachineDelayData.length === 0) {
          // console.warn("Boş veri geldi.");
          setMachineDelayTimeData({ labels: [], values: [] });
          return;
        }

        // Veriyi işleme
        setMachineDelayTimeData({
          labels: allMachineDelayData.map((item) => {
            const date = new Date(item.createdTime);
            return `${date.getUTCHours().toString().padStart(2, "0")}:${date
              .getUTCMinutes()
              .toString()
              .padStart(2, "0")}`;
          }),
          values: allMachineDelayData.map((item) => {
            const parts = item.cycleTime.split(":").map(Number); // "0:00:04" -> [0, 0, 4]
            const seconds = parts.reduce(
              (acc, part, index) =>
                acc + part * Math.pow(60, parts.length - 1 - index),
              0
            ); // Saniyeye çevir
            return seconds;
          }), // `machineCycleTime` string ise float'a çevrilen kısım
        });
      } catch (error) {
        console.error("Veri hatası:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchMachineDelayTimes();
  }, [apiUrl, machineId, timeRange]);

  useEffect(() => {
    if (!apiUrl || !machineId) return;

    const fetchStoppedTimes = async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `${apiUrl}/api/stoppedTimes?range=today&machineId=${machineId}`
        );
        if (!response.ok) {
          throw new Error("Verileri getirme hatası");
        }

        const allStoppedTimesData = await response.json();

        if (!allStoppedTimesData || allStoppedTimesData.length === 0) {
          // console.warn("Boş veri geldi.");
          setStopped({ labels: [], values: [] });
          return;
        }

        // Veriyi işleme
        setStopped({
          labels: allStoppedTimesData.map((item) => {
            const date = new Date(item.createdTime);
            return `${date.getUTCHours().toString().padStart(2, "0")}:${date
              .getUTCMinutes()
              .toString()
              .padStart(2, "0")}`;
          }),
          values: allStoppedTimesData.map((item) => {
            const parts = item.stoppedTime.split(":").map(Number); // "0:00:04" -> [0, 0, 4]
            const seconds = parts.reduce(
              (acc, part, index) =>
                acc + part * Math.pow(60, parts.length - 1 - index),
              0
            ); // Saniyeye çevir
            return seconds;
          }), // `stoppedTime` string ise float'a çevrilen kısım
        });
      } catch (error) {
        console.error("Veri hatası:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStoppedTimes();
  }, [apiUrl, machineId, timeRange]);

  useEffect(() => {
    if (!apiUrl || !machineId) return;

    const fetchShortStoppedTimes = async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `${apiUrl}/api/shortStoppedTimes?range=today&machineId=${machineId}`
        );
        if (!response.ok) {
          throw new Error("Verileri getirme hatası");
        }

        const allShortStoppedTimesData = await response.json();

        if (
          !allShortStoppedTimesData ||
          allShortStoppedTimesData.length === 0
        ) {
          // console.warn("Boş veri geldi.");
          setShortStopped({ labels: [], values: [] });
          return;
        }

        // Veriyi işleme
        setShortStopped({
          labels: allShortStoppedTimesData.map((item) => {
            const date = new Date(item.createdTime);
            return `${date.getUTCHours().toString().padStart(2, "0")}:${date
              .getUTCMinutes()
              .toString()
              .padStart(2, "0")}`;
          }),
          values: allShortStoppedTimesData.map((item) => {
            const parts = item.shortStoppedTime.split(":").map(Number); // "0:00:04" -> [0, 0, 4]
            const seconds = parts.reduce(
              (acc, part, index) =>
                acc + part * Math.pow(60, parts.length - 1 - index),
              0
            ); // Saniyeye çevir
            return seconds;
          }), // `stoppedTime` string ise float'a çevrilen kısım
        });
      } catch (error) {
        console.error("Veri hatası:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchShortStoppedTimes();
  }, [apiUrl, machineId, timeRange]);

  return (
    <div className="min-h-screen bg-gray-100 p-6 space-y-6">
      {/* Üst Kartlar */}
      <MachineInfoCard machineInfo={machineInfo} />

      {/* Alt Kısım */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <PieChartMachineStatus
          cycleTimeData={cycleTimeData}
          stoppedData={stopped}
          shortStoppedData={shortStopped}
        />

        <div className="space-y-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {orderInfo.map((info, index) => (
              <div
                key={index}
                className="bg-white rounded-lg shadow-md p-4 border border-gray-200 h-28 flex flex-col justify-center"
              >
                <div className="text-center text-sm font-semibold text-gray-500">
                  {info.title}
                </div>
                <hr className="my-2 border-gray-300" />
                <div className="text-center text-2xl font-bold text-gray-800">
                  {info.value.includes("/") ? (
                    <>
                      {info.value.split("/")[0]}/
                      <span className="text-gray-500 text-opacity-70">
                        {info.value.split("/")[1]}
                      </span>
                    </>
                  ) : (
                    info.value
                  )}
                </div>
              </div>
            ))}
          </div>
          {/* Zaman Çizelgesi ve ComboBox */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-700">
              Zaman Çizelgesi
            </h3>
            <select
              className="border border-gray-300 rounded-md p-2 focus:ring-2 focus:ring-blue-500"
              value={timeRange}
              onChange={handleTimeRangeChange}
            >
              <option value="last-30-minutes">Son 30 Dakika</option>
              <option value="last-hour">Son 1 Saat</option>
              <option value="today">Bugün</option>
              <option value="last-week">Son 1 Hafta</option>
              <option value="last-month">Son 1 Ay</option>
            </select>
          </div>
          <div style={{ minHeight: "300px" }}>
            {loading ? (
              <div className="flex justify-center items-center h-full">
                <Spin size="large" /> {/* Yükleme göstergesi */}
              </div>
            ) : (
              <ProductLineChart data={productData} />
            )}
          </div>
        </div>
      </div>

      {/* Çevrim Süresi */}
      <CycleTimeLineChart data={cycleTimeData} />

      {/* Çevrim Süresi Bilgilendirme Kartları */}
      <CycleTimeSummaryCards
        cycleTimeData={cycleTimeData}
        machineCycleTimeData={machineCycleTimeData}
        machineDelayTimeData={machineDelayTimeData}
      />

      {/* Son 10 Gün Üretim */}
      <LastTenDaysProductionChart data={lastTenDaysData} />
    </div>
  );
};

export default MachineDetailsPage;
